import banner from "../../../courses/module-1/img4.png";

const modulo4_pt = {
  courseId: "finanzas-personales",
  id: "modulo-4", // ⚠️ Mesmo ID
  title: "📘 MÓDULO 4: Uso Responsável do Crédito",
  estimatedDuration: "2 horas",
  format: "Leitura + atividade de análise + avaliação prática",
  bannerUrl: banner,
  sections: [
    {
      id: "objetivos",
      navLabel: "Objetivos",
      title: "🎯 Objetivos do módulo",
      content: [
        "Entender o que é crédito e como utilizá-lo de forma responsável no Brasil.",
        "Conhecer o funcionamento do Serasa, SPC e o Score de Crédito.",
        "prender a identificar créditos perigosos, golpes e juros abusivos.",
      ],
    },
    {
      id: "lectura",
      navLabel: "Leitura principal",
      title: "📖 Leitura principal: O que é crédito e como usá-lo bem?",
      content: [
        {
          type: "paragraph",
          text: "📌 O que é crédito?",
        },
        {
          type: "paragraph",
          text: "Crédito é dinheiro emprestado por uma instituição (banco, fintech, varejo, financeira) com o compromisso de devolver o valor dentro de um prazo — geralmente com juros e outras tarifas.",
        },
        {
          type: "paragraph",
          text: "Em palavras simples:",
        },
        {
          type: "paragraph",
          text: "👉 Crédito não é dinheiro extra. É dinheiro emprestado, e pode custar caro se você não usar com cuidado.",
        },
      ],
    },
    {
      id: "util",
      navLabel: "Pode ser útil?",
      title: "✅ Quando o crédito pode ser útil?",
      content: [
        {
          type: "list",
          items: [
            "Para compras grandes (celular, notebook, eletrodomésticos).",
            "Para emergências reais quando você não tem reserva financeira.",
            "Para construir seu score, se você usa de forma responsável.",
          ],
        },
      ],
    },
    {
      id: "problema",
      navLabel: "Pode ser um problema?",
      title: "❌ Quando o crédito vira problema?",
      content: [
        {
          type: "list",
          items: [
            "Quando você gasta mais do que ganha.",
            "Quando paga apenas o mínimo da fatura.",
            "Quando usa crédito como extensão da renda.",
            "Quando aceita crédito sem entender os juros, tarifas e multas.",
          ],
        },
        {
          type: "paragraph",
          text: "No Brasil, muitas pessoas começam a se endividar muito cedo por falta de informação, não por irresponsabilidade.",
        },
      ],
    },
    {
      id: "exemplo-real",
      navLabel: "Exemplo real",
      title: "🧠 Exemplo real",
      content: [
        {
          type: "paragraph",
          text: "Larissa, 21 anos, estudante em Recife, conseguiu uma tarjeta de crédito de uma fintech com limite de R$ 1.200.",
        },
        {
          type: "paragraph",
          text: "Usou quase tudo em comida por delivery e roupas — sem verificar juros e tarifas.",
        },
        {
          type: "paragraph",
          text: "Como atrasou duas faturas, acumulou mais de R$ 350 em juros e multa em poucos meses.",
        },
        {
          type: "paragraph",
          text: "Hoje, ela paga mais do que gastou e ainda teve o score reduzido, dificultando financiamentos futuros.",
        },
        {
          type: "list",
          items: ["Ter cartão não significa ter dinheiro sobrando."],
        },
      ],
    },
    {
      id: "complementaria",
      navLabel: "Leitura complementar",
      title: "📖 Leitura complementar: Serasa, SPC e Score",
      content: [
        {
          type: "paragraph",
          text: "No Brasil, Serasa, SPC Brasil e Boa Vista registram como você usa o crédito:",
        },
        {
          type: "list",
          items: [
            "Se paga em dia",
            "Se atrasa",
            "Se tem dívidas abertas",
            "Histórico de empréstimos, cartões, financiamentos",
            "Contas de consumo (telefone, energia etc.)",
          ],
        },
        {
          type: "paragraph",
          text: "📌 Como isso afeta você?",
        },
        {
          type: "list",
          items: [
            "Score alto: maior facilidade para conseguir crédito e pagar juros menores.",
            "Score baixo: empréstimos negados ou juros altos.",
            "Sem histórico: é como ser “invisível” para o sistema financeiro.",
          ],
        },
        {
          type: "paragraph",
          text: "👉 Ter score não é ruim — o problema é ter um score ruim.",
        },
      ],
    },
    {
      id: "identificacion",
      navLabel: "Identificar créditos",
      title: "🛠 Como identificar créditos perigosos",
      content: [
        {
          type: "paragraph",
          text: "Antes de aceitar qualquer crédito, confira:",
        },
        {
          type: "orderedList",
          items: [
            "CET (Custo Efetivo Total) — equivalente ao CAT, obrigatório por lei no Brasil.",
            "Taxas de juros (mensal e anual).",
            "Tarifas escondidas: manutenção, atraso, emissão, avaliação, IOF.",
            "Cláusulas do contrato: multas, renovação automática, cobranças extras.",
            "A procedência:",
          ],
        },
        {
          type: "list",
          items: [
            "Evite empréstimos oferecidos por WhatsApp, mensagens, perfis falsos, “depósito antecipado”.",
          ],
        },
        {
          type: "paragraph",
          text: "💡 Dica: Se você não entende ou não tem acesso às condições completas… não assine nada ainda.",
        },
      ],
    },
    {
      id: "guia",
      navLabel: "Mini guia",
      title: "💬 Mini guia: bom uso do crédito",
      content: [
        {
          type: "paragraph",
          text: "✅ Pague sempre o valor total da fatura (evite o rotativo).",
        },
        {
          type: "paragraph",
          text: "✅ Use no máximo 30% do limite da sua tarjeta.",
        },
        {
          type: "paragraph",
          text: "✅ Ative lembretes de pagamento no celular.",
        },
        {
          type: "paragraph",
          text: "✅ Não faça um empréstimo para pagar outro.",
        },
        {
          type: "paragraph",
          text: "✅ Consulte gratuitamente seu CPF:",
        },
        {
          type: "list",
          items: [
            "Serasa",
            "Registrato (Banco Central)",
            "Aplicativos dos birôs de crédito",
          ],
        },
      ],
    },
    {
      id: "extra",
      navLabel: "Extra",
      title: "📊 Extra: A Lei de Pareto e o uso do crédito",
      content: [
        {
          type: "paragraph",
          text: "📌 O que é?",
        },
        {
          type: "paragraph",
          text: "A regra do 80/20 mostra que 20% das suas ações produzem 80% dos resultados.",
        },
        {
          type: "paragraph",
          text: "🔗 Como isso se conecta com crédito?",
        },
        {
          type: "list",
          items: [
            "20% das suas decisões financeiras geram a maioria dos seus problemas… ou benefícios.",
            "Um único erro (ex.: atraso) pode derrubar seu score.",
            "Uma única ação positiva (ex.: sempre pagar em dia) pode fortalecer todo seu histórico.",
          ],
        },
      ],
    },
    {
      id: "na-pratica",
      navLabel: "Na prática",
      title: "🎯 Como aplicar isso na prática?",
      content: [
        {
          type: "paragraph",
          text: "1. Identifique os hábitos que mais prejudicam seu bolso.",
        },
        {
          type: "list",
          items: [
            "Ex.: esquecer o vencimento, gastar por impulso, não ler contratos.",
          ],
        },
        {
          type: "paragraph",
          text: "2. Mude apenas 1 ou 2 comportamentos primeiro.",
        },
        {
          type: "list",
          items: ["Ex.: usar só 30% do limite, colocar lembretes no celular.."],
        },
        {
          type: "paragraph",
          text: "3. Busque grandes resultados com pequenas mudanças.",
        },
        {
          type: "list",
          items: [
            "Você não precisa ser especialista — só precisa tomar boas decisões nos momentos certos.",
          ],
        },
      ],
    },
    {
      id: "actividad",
      navLabel: "Atividade prática",
      title: "📝 Avaliação: atividade prática",
      content: [
        {
          type: "paragraph",
          text: "Atividade: Avaliar e comentar uma oferta real de cartão de crédito",
        },
        "O que você deve fazer?",
        {
          type: "paragraph",
          text: "1. Pesquise um cartão de crédito real (no site de um banco ou loja).",
        },
        {
          type: "paragraph",
          text: "2. Copie as informações principais:",
        },
        {
          type: "list",
          items: [
            "Limite de crédito inicial",
            "CET médio",
            "Juros anuais",
            "Taxas e tarifas",
            "Requisitos para obtê-lo",
          ],
        },
        {
          type: "paragraph",
          text: "3. Analise e responda:",
        },
        {
          type: "list",
          items: [
            "Este cartão é vantajoso para mim como estudante? Por quê?",
            "Quais riscos existem se eu não o usar corretamente?",
            "Que alternativas mais seguras posso considerar?",
          ],
        },
      ],
    },
    {
      id: "bibliografia",
      navLabel: "Bibliografia",
      title: "📚 Bibliografia recomendada",
      content: [
        "Nath Finanças – “Orçamento sem falhas”",
        "Banco Central – Cartilha de Educação Financeira",
        "Serasa – Guia de Crédito e Score",
        "Me Poupe! (Nathalia Arcuri) – Canal e blog",
        "Febraban – Guia de Uso Consciente do Crédito",
      ],
    },
    {
      id: "frase",
      navLabel: "Frase do dia",
      title: "🧠 Frase do dia",
      content: [
        "“Crédito não é vilão. O problema é usá-lo sem entender o custo real.”",
      ],
    },
  ],
};

export default modulo4_pt;
